import React, { useState } from "react";
import { useQuery, gql } from "@apollo/client";
import { isoDateToMDY } from "../../../utils/common";
import { CreateNote } from "./modals/CreateNote";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Plus, FileText } from "lucide-react";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";
import { TabToolbar } from "../shared/components/TabToolbar";
import { Input } from "@/components/ui/input";
import { TabTable } from "../shared/components/TabTable";

const GET_NOTES_DATA = gql`
  query GetNotesData($odrnumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrnumber) {
      notes {
        note
        date
        author
        program
      }
    }
  }
`;

interface Note {
  note: string;
  date: string;
  author: string;
  program: string;
}

interface NotesData {
  getParticipantByODRNumber: {
    notes: Note[];
  };
}

interface NotesProps {
  odrnumber: number;
  programNumber: string;
}

const LoadingState = () => (
  <Card>
    <CardHeader>
      <CardTitle>Notes</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    </CardContent>
  </Card>
);

const ErrorState = ({ error }: { error: Error }) => (
  <Alert variant="destructive">
    <AlertCircle className="h-4 w-4" />
    <AlertDescription>
      Error loading notes data: {error.message}
    </AlertDescription>
  </Alert>
);

export const Notes: React.FC<NotesProps> = ({ odrnumber, programNumber }) => {
  const [showAddNote, setShowAddNote] = useState(false);
  const [search, setSearch] = useState("");

  const { loading, error, data, refetch } = useQuery<NotesData>(GET_NOTES_DATA, {
    variables: { odrnumber },
    skip: !odrnumber,
  });

  if (loading) return <TabSpinner label="Loading notes..." />;
  if (error) return <ErrorState error={error} />;
  if (!data) return null;

  const notesData = data.getParticipantByODRNumber.notes || [];
  const filteredNotes = notesData.filter((note) => {
    const term = search.toLowerCase();
    return (
      note.note.toLowerCase().includes(term) ||
      note.author.toLowerCase().includes(term) ||
      note.program.toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-4">
      <TabHeader
        icon={FileText}
        title="Notes"
        subtitle="Program notes entered on this participant"
        action={
          <Button 
            onClick={() => setShowAddNote(true)}
            size="sm"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Note
          </Button>
        }
      />
      <TabToolbar
        left={
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notes..."
            className="h-9 w-60"
          />
        }
      />
      <Card className="rounded-none border-0 shadow-none">
        <CardContent className="p-0">
          <div className="border-t border-b border-gray-200 overflow-hidden">
          {filteredNotes.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No notes found for this participant.
            </p>
          ) : (
            <TabTable<Note>
              data={filteredNotes}
              emptyMessage="No notes found for this participant."
              columns={[
                { title: "Date", className: "w-[140px]", render: (n) => isoDateToMDY(n.date) },
                { title: "Program", className: "w-[120px]", render: (n) => n.program.toUpperCase() },
                { title: "Note", render: (n) => n.note },
                { title: "Author", className: "w-[160px]", render: (n) => n.author },
              ]}
            />
          )}
          </div>
        </CardContent>
      </Card>

      {showAddNote && (
        <CreateNote 
          onClose={() => setShowAddNote(false)} 
          odrnumber={odrnumber} 
          programNumber={programNumber}
          refetch={refetch}
        />
      )}
    </div>
  );
};
